import {
  Business,
  Close,
  Face,
  Logout,
  QuestionAnswer,
  Tour,
} from '@mui/icons-material';
import MenuIcon from '@mui/icons-material/Menu';
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Drawer,
  Icon,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from '@mui/material';
import { MouseEventHandler, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import useStore from '../stores';
import logoImage from '../assets/gg-black.svg';
import imageSlide3 from '../assets/gg__illustration--co-working-space-3.svg';
import imageSlide0 from '../assets/gg__illustration--conversation-businessman-customer-1.svg';
import imageSlide2 from '../assets/gg__illustration--digital-nomad-working-2.svg';
import imageSlide1 from '../assets/gg__illustration--wireframe-4.svg';
import { brandColors, neutralColors } from '../theme/colors';
import SiteMessageBanner from './SiteMessageBanner';

type ResponsiveNavBarProps = {
  handleSignOut: MouseEventHandler<HTMLAnchorElement> | undefined;
};

function ResponsiveNavBar(props: ResponsiveNavBarProps) {
  const { handleSignOut } = props;
  const location = useLocation();
  const excludedPaths = useMemo(() => ['/tou', '/setup'], []);

  const userProfile = useStore((state) => state.userProfile);
  const {
    verified = false,
    first_name = '',
    last_name = '',
  } = userProfile || {};

  const [openDrawer, setOpen] = useState(false);
  const [currentOnboardingSlideIndex, setCurrentOnboardingSlideIndex] =
    useState(0);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const navigate = useNavigate();
  const toggleDrawer = (newOpen: boolean) => () => {
    setOpen(newOpen);
  };
  const [showTopBannerMessage, setShowTopBannerMessage] = useState(false);
  const [isOnboardingDialogOpen, setOnboardingDialogOpen] = useState(false);

  const getUserInitials = (firstName: string, lastName: string) => {
    const firstInitial = firstName?.charAt(0) || '';
    const lastInitial = lastName?.charAt(0) || '';
    return `${firstInitial}${lastInitial}`;
  };

  const userInitials = getUserInitials(first_name, last_name);

  const navItems = [
    {
      id: 'grantees',
      url: '/grantees?unarchivedOnly=true',
      text: 'Grantees',
    },
    {
      id: 'analysis',
      url: '/analysis?unarchivedOnly=true',
      text: 'Analyses',
    },
    {
      id: 'riskProfile',
      url: '/risk-profile?unarchivedOnly=true',
      text: 'Financial profile',
    },
  ];

  const userItems = [
    {
      id: userProfile?.is_admin ? 'organization' : 'user',
      url: userProfile?.is_admin ? '/admin/settings' : '/profile',
      text: userProfile?.is_admin ? 'Admin settings' : 'Settings',
      icon: userProfile?.is_admin ? (
        <Business fontSize="small" />
      ) : (
        <Face fontSize="small" />
      ),
    },
    {
      id: 'user',
      url: '/Help',
      text: 'Help center',
      icon: <QuestionAnswer fontSize="small" />,
    },
    {
      id: 'signOut',
      url: '/',
      text: 'Sign out',
      icon: <Logout fontSize="small" />,
    },
  ];

  const onboardingContent = [
    {
      img: imageSlide0,
      title: 'Welcome to Grant Guardian',
      content:
        'We’re excited to have you on board! Let’s walk you through some key features to help you get started quickly and make the most of your experience.',
    },
    {
      img: imageSlide1,
      title: 'Create your analysis',
      content:
        'Start by creating an analysis tailored to your needs. Use our default financial profile or customize your own to better assess financial due diligence.',
    },
    {
      img: imageSlide2,
      title: 'Manage your financial profiles',
      content:
        'Easily track and manage your financial due diligence assessments with personalized financial profiles, helping you stay organized and informed.',
    },
    {
      img: imageSlide3,
      title: 'Explore the help center',
      content:
        'Get the most out of our platform with our Help Center. Browse FAQs, follow step-by-step guides, and watch video tutorials to quickly learn key features and tools.',
    },
  ];

  const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (url: string) => {
    if (url) {
      navigate(url);
    }
    handleMenuClose();
  };

  const handleTopBannerMessageClose = () => {
    setShowTopBannerMessage(false);
    localStorage.setItem('watchVideosMessageClosed', 'true');
  };

  const toggleOnboardingCompleted = () => {
    localStorage.setItem('onboardingCompleted', JSON.stringify(true));
    setOnboardingDialogOpen(false);
  };

  const handleOnboardingNextSlide = () => {
    setCurrentOnboardingSlideIndex(
      (prevIndex) => (prevIndex + 1) % onboardingContent.length,
    );
  };

  const handleOnboardingPrevSlide = () => {
    setCurrentOnboardingSlideIndex(
      (prevIndex) =>
        (prevIndex - 1 + onboardingContent.length) % onboardingContent.length,
    );
  };

  useEffect(() => {
    const watchVideosMessageClosed = localStorage.getItem(
      'watchVideosMessageClosed',
    );
    const onboardingCompleted = localStorage.getItem('onboardingCompleted');

    if (watchVideosMessageClosed) {
      setShowTopBannerMessage(false);
    } else {
      setShowTopBannerMessage(true);
    }

    if (
      (onboardingCompleted === null &&
        !excludedPaths.includes(location.pathname)) ||
      (onboardingCompleted === 'false' &&
        !excludedPaths.includes(location.pathname))
    ) {
      setOnboardingDialogOpen(true);
    }
  }, [location.pathname, excludedPaths]);

  return (
    <>
      <Box className="body__skip-link" component="a" href="#main-content">
        Skip to Main Content
      </Box>
      <SiteMessageBanner />
      {showTopBannerMessage && !excludedPaths.includes(location.pathname) && (
        <AppBar variant="message" position="sticky" role="banner">
          <Container
            maxWidth={false}
            sx={{
              maxWidth: '1320px',
            }}
          >
            <Toolbar disableGutters>
              <div className="d-flex flex-grow-1 align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-3">
                  <Typography
                    color={`${neutralColors.white}`}
                    variant="subtitle1"
                  >
                    Welcome to Grant Guardian. To learn more about how to use
                    this product, take a look at our video tutorial library.
                  </Typography>
                  <Button component={Link} variant="contained" to="/help">
                    Watch videos
                  </Button>
                </div>
                <IconButton
                  aria-label="close"
                  color="secondary"
                  onClick={handleTopBannerMessageClose}
                >
                  <Close />
                </IconButton>
              </div>
            </Toolbar>
          </Container>
        </AppBar>
      )}
      <AppBar variant="default" position="static" role="banner">
        <Container
          maxWidth={false}
          sx={{
            maxWidth: '1320px',
          }}
        >
          <Toolbar
            disableGutters
            component="nav"
            role="navigation"
            aria-label="Main navigation"
          >
            <Box sx={{ flexGrow: 1, display: { xs: 'flex' } }}>
              {verified ? (
                <Typography variant="h6" noWrap component={Link} to="/">
                  <Icon
                    sx={{
                      display: 'flex',
                      mr: 1,
                      height: '40px',
                      width: '160px',
                    }}
                  >
                    <img src={logoImage} alt="Grant Guardian logo" />
                  </Icon>
                </Typography>
              ) : (
                <Typography variant="h6" noWrap>
                  <Icon
                    sx={{
                      display: 'flex',
                      mr: 1,
                      height: '40px',
                      width: '160px',
                    }}
                  >
                    <img src={logoImage} alt="Grant Guardian logo" />
                  </Icon>
                </Typography>
              )}
              {verified && (
                <Box sx={{ flexGrow: 1, display: { xs: 'none', sm: 'flex' } }}>
                  {navItems.map((item) => (
                    <Button
                      component={Link}
                      key={item.id}
                      className="text-capitalize"
                      to={item.url}
                      color="secondary"
                      aria-label={`Navigate to ${item.text}`}
                      tabIndex={0}
                    >
                      {item.text}
                    </Button>
                  ))}
                </Box>
              )}
            </Box>
            {!verified ? (
              <Box sx={{ display: { xs: 'flex', sm: 'flex' } }}>
                <Button
                  component="a"
                  onClick={handleSignOut}
                  color="secondary"
                  aria-label="Sign out"
                  tabIndex={0}
                >
                  Sign Out
                </Button>
              </Box>
            ) : (
              <>
                <Box sx={{ display: { xs: 'flex', sm: 'none' } }}>
                  <IconButton
                    className="me-2"
                    size="large"
                    edge="start"
                    aria-label="Open menu"
                    color="secondary"
                    onClick={toggleDrawer(true)}
                    tabIndex={0}
                  >
                    <MenuIcon />
                  </IconButton>
                </Box>
                <Box
                  sx={{
                    display: {
                      xs: 'none',
                      sm: 'flex',
                      gap: 8,
                      alignItems: 'center',
                    },
                  }}
                >
                  <Button
                    component={Link}
                    className="text-capitalize"
                    to="/help"
                    color="secondary"
                    aria-label="Go to Help Center"
                    variant="contained"
                    endIcon={<QuestionAnswer />}
                    tabIndex={0}
                  >
                    Help Center
                  </Button>
                  <Button
                    className="text-capitalize"
                    onClick={() => setOnboardingDialogOpen(true)}
                    color="secondary"
                    aria-label="Start the onboarding tour"
                    variant="contained"
                    endIcon={<Tour />}
                    tabIndex={0}
                  >
                    Start tour
                  </Button>
                  <IconButton
                    onClick={handleMenuClick}
                    aria-controls={open ? 'user-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? 'true' : undefined}
                    sx={{
                      background: 'black',
                      backgroundImage: `linear-gradient(to bottom right, ${brandColors.mcGovernGold} 30%, ${brandColors.mcGovernRust} 50%, ${brandColors.mcGovernTeal} 100%)`,
                      padding: '0.125rem', // 2px
                      '&:hover': {
                        backgroundColor: 'transparent',
                      },
                    }}
                    tabIndex={0}
                  >
                    <Avatar
                      sx={{
                        fontWeight: 800,
                        fontSize: '1rem',
                        background: `${neutralColors.black}`,
                      }}
                    >
                      {userInitials}
                    </Avatar>
                  </IconButton>
                  <Menu
                    anchorEl={anchorEl}
                    id="user-menu"
                    open={open}
                    onClose={handleMenuClose}
                    onClick={handleMenuClose}
                    slotProps={{
                      paper: {
                        elevation: 1,
                        sx: {
                          width: 200,
                          overflow: 'visible',
                          border: 'none',
                          filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.16))',
                          '& .MuiAvatar-root': {
                            width: 32,
                            height: 32,
                            ml: -0.5,
                            mr: 1,
                          },
                        },
                      },
                    }}
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                    disableEnforceFocus
                  >
                    {userItems.map((item, index) => [
                      index === userItems.length - 1 ? (
                        <Divider key={`divider-${item.id}`} />
                      ) : null,
                      <MenuItem
                        key={`menuitem-${item.id}`}
                        href={item.url}
                        onClick={
                          item.id === 'signOut'
                            ? handleSignOut
                            : () => handleMenuItemClick(item.url)
                        }
                        title={item.text}
                        aria-describedby={item.text}
                      >
                        <ListItemIcon>{item.icon}</ListItemIcon>
                        {item.text}
                      </MenuItem>,
                    ])}
                  </Menu>
                </Box>
                <Drawer
                  open={openDrawer}
                  onClose={toggleDrawer(false)}
                  role="navigation"
                  tabIndex={0}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      height: 1,
                      width: 250,
                    }}
                    role="presentation"
                    onClick={toggleDrawer(false)}
                  >
                    <List
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                      }}
                    >
                      {navItems.map((item) => (
                        <ListItem key={item.id} disablePadding>
                          <ListItemButton
                            component="a"
                            href={item.url}
                            tabIndex={0}
                          >
                            <ListItemText primary={item.text} />
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                    <List sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Divider />
                      {userItems.map((item) => (
                        <ListItem key={item.id} disablePadding>
                          <ListItemButton
                            href={item.url}
                            component="a"
                            onClick={
                              item.id === 'signOut' ? handleSignOut : () => null
                            }
                            tabIndex={0}
                          >
                            <ListItemIcon>{item.icon}</ListItemIcon>
                            {item.text}
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                </Drawer>
              </>
            )}
          </Toolbar>
        </Container>
      </AppBar>
      <Dialog
        fullWidth
        maxWidth="xs"
        open={isOnboardingDialogOpen}
        onClose={toggleOnboardingCompleted}
        aria-labelledby="onBoardingDialogTitle"
        aria-describedby="onBoardingDialogDescription"
      >
        <DialogTitle>
          {onboardingContent[currentOnboardingSlideIndex].title}
        </DialogTitle>
        <DialogContent>
          <div className="d-flex flex-column gap-4">
            <Typography
              variant="subtitle2"
              color={`${neutralColors.tailwind600}`}
            >
              {onboardingContent[currentOnboardingSlideIndex].content}
            </Typography>
            <figure className="d-flex justify-content-center">
              <img
                className="onboarding__slide-image img-fluid w-75"
                src={onboardingContent[currentOnboardingSlideIndex].img}
                alt={`Slide ${currentOnboardingSlideIndex + 1}`}
              />
            </figure>
          </div>
        </DialogContent>
        <DialogActions>
          <div className="d-flex flex-grow-1 align-items-center justify-content-between">
            <Typography variant="body2" color={`${neutralColors.tailwind400}`}>
              {`${currentOnboardingSlideIndex + 1} of ${
                onboardingContent.length
              }`}
            </Typography>
            <div className="d-flex gap-2">
              {currentOnboardingSlideIndex === 0 ? (
                <Button onClick={toggleOnboardingCompleted}>Skip</Button>
              ) : (
                <Button onClick={handleOnboardingPrevSlide}>Back</Button>
              )}
              {currentOnboardingSlideIndex < onboardingContent.length - 1 ? (
                <Button
                  onClick={handleOnboardingNextSlide}
                  variant="contained"
                  color="primary"
                >
                  Continue
                </Button>
              ) : (
                <Button
                  onClick={toggleOnboardingCompleted}
                  variant="contained"
                  color="primary"
                >
                  Finish
                </Button>
              )}
            </div>
          </div>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default ResponsiveNavBar;
